import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import PhotoUpload from '../components/PhotoUpload';
import { generatePhotos, getCategoryFromPath } from '../config/photos';

interface Photo {
  id: number;
  src: string;
  category: string;
  caption: string;
  date: string;
}

interface Video {
  id: number;
  src: string;
  title: string;
  date: string;
  thumbnail?: string;
}

const Gallery: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'photos' | 'videos'>('photos');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState<Photo[]>(() => {
    // Load uploaded photos from localStorage on component mount
    const saved = localStorage.getItem('uploadedPhotos');
    return saved ? JSON.parse(saved) : [];
  });
  const [playingVideoId, setPlayingVideoId] = useState<number | null>(null);
  const [pausedVideoId, setPausedVideoId] = useState<number | null>(null);
  const videoRefs = useRef<{ [key: number]: HTMLVideoElement | null }>({});

  // Videos array - add your video paths here
  const videos: Video[] = useMemo(() => [
    { id: 1, src: '/us.mov', title: 'Our Moments', date: '2024' },

    // Add more videos as needed
     { id: 2, src: '/us2.mov', title: 'Birthdays', date: '2024' },
     { id: 3, src: '/us3.mov', title: 'Niagara Falls', date: '2024' },
     { id: 4, src: '/us4.mov', title: 'New York', date: '2024' },
     { id: 5, src: '/us5.mov', title: 'Vancouver', date: '2024' },

  ], []);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle video play/pause events
  useEffect(() => {
    const cleanupFunctions: (() => void)[] = [];

    videos.forEach(video => {
      const videoElement = videoRefs.current[video.id];
      if (videoElement) {
        const playHandler = () => {
          if (playingVideoId === video.id) {
            setPausedVideoId(null);
          }
        };
        
        const pauseHandler = () => {
          if (playingVideoId === video.id) {
            setPausedVideoId(video.id);
          }
        };
        
        videoElement.addEventListener('play', playHandler);
        videoElement.addEventListener('pause', pauseHandler);

        cleanupFunctions.push(() => {
          videoElement.removeEventListener('play', playHandler);
          videoElement.removeEventListener('pause', pauseHandler);
        });
      }
    });

    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [videos, playingVideoId]);

  // Handle sequential video playback
  useEffect(() => {
    if (playingVideoId === null || videos.length === 0) return;
    
    // Don't auto-play if video is paused
    if (pausedVideoId === playingVideoId) return;

    const currentVideo = videoRefs.current[playingVideoId];
    if (!currentVideo) return;

    const playNextVideo = () => {
      const currentIndex = videos.findIndex(v => v.id === playingVideoId);
      if (currentIndex === -1) return;

      // Find next video (loop back to first if at end)
      const nextIndex = (currentIndex + 1) % videos.length;
      const nextVideoId = videos[nextIndex].id;
      
      // Stop current video and play next
      currentVideo.pause();
      setPausedVideoId(null);
      setPlayingVideoId(nextVideoId);
      
      // Play next video after a brief delay
      setTimeout(() => {
        const nextVideo = videoRefs.current[nextVideoId];
        if (nextVideo) {
          nextVideo.play().catch(error => {
            console.log('Could not play next video:', error);
          });
        }
      }, 500);
    };

    const handleVideoEnd = () => {
      // Only auto-advance if video wasn't paused
      if (pausedVideoId !== playingVideoId) {
        playNextVideo();
      }
    };

    currentVideo.addEventListener('ended', handleVideoEnd);

    // Play current video only if not paused
    if (pausedVideoId !== playingVideoId && currentVideo.paused) {
      currentVideo.play().catch((error) => {
        console.log('Could not play video:', error);
      });
    }

    return () => {
      currentVideo.removeEventListener('ended', handleVideoEnd);
    };
  }, [playingVideoId, pausedVideoId, videos]);

  // Auto-generate photos from config with folder-based categorization
  const photos: Photo[] = useMemo(() => generatePhotos(), []);

  // Dynamically get categories from photos (excluding 'Uploaded' for now, will add it separately)
  const availableCategories = useMemo(() => {
    const cats = new Set<string>(['All']);
    photos.forEach(photo => {
      if (photo.category && photo.category !== 'Uploaded') {
        cats.add(photo.category);
      }
    });
    // Add Uploaded category if there are uploaded photos
    if (uploadedPhotos.length > 0) {
      cats.add('Uploaded');
    }
    return Array.from(cats);
  }, [photos, uploadedPhotos.length]);

  const categories = availableCategories;

  const allPhotos = [...photos, ...uploadedPhotos];
  const filteredPhotos = selectedCategory === 'All' 
    ? allPhotos 
    : allPhotos.filter(photo => photo.category === selectedCategory);

  const openLightbox = (photo: Photo) => {
    setSelectedPhoto(photo);
    setCurrentIndex(filteredPhotos.findIndex(p => p.id === photo.id));
  };

  const navigatePhoto = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setCurrentIndex(currentIndex > 0 ? currentIndex - 1 : filteredPhotos.length - 1);
    } else {
      setCurrentIndex(currentIndex < filteredPhotos.length - 1 ? currentIndex + 1 : 0);
    }
  };

  const handlePhotoUpload = (files: File[]) => {
    const newPhotos: Photo[] = files.map((file, index) => {
      // Auto-categorize uploaded photos based on filename
      const category = getCategoryFromPath(file.name);
      return {
        id: Date.now() + index,
        src: URL.createObjectURL(file),
        category: category,
        caption: file.name.replace(/\.[^/.]+$/, ""), // Remove file extension
        date: new Date().toLocaleDateString()
      };
    });

    setUploadedPhotos(prev => {
      const updated = [...newPhotos, ...prev];
      // Save to localStorage for persistence
      localStorage.setItem('uploadedPhotos', JSON.stringify(updated));
      return updated;
    });
    setShowUpload(false);
    
    // Show success message
    alert(`${files.length} photo(s) uploaded successfully!`);
  };

  const VideoCard: React.FC<{ 
    video: Video; 
    index: number;
    isPlaying: boolean;
    isPaused: boolean;
    onPlay: () => void;
    onPause: () => void;
    videoRef: (el: HTMLVideoElement | null) => void;
  }> = ({ video, index, isPlaying, isPaused, onPlay, onPause, videoRef }) => {
    const handleClick = () => {
      if (isPlaying && !isPaused) {
        // If playing, pause it
        onPause();
      } else {
        // If paused or not playing, play it
        onPlay();
      }
    };

    const showPlayButton = !isPlaying || isPaused;

    return (
      <div
        className="group cursor-pointer relative animate-fade-in"
        style={{ 
          animationDelay: `${index * 50}ms`,
          animationFillMode: 'both'
        }}
        onClick={handleClick}
      >
        <div className="relative overflow-hidden rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow duration-300 bg-black">
          <video
            ref={videoRef}
            src={video.src}
            className="w-full h-96 md:h-[32rem] object-cover"
            muted={false}
            playsInline
            loop={false}
            preload="metadata"
          />
          
          {/* Overlay with play button */}
          <div 
            className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-200"
            style={{ 
              opacity: isPlaying && !isPaused ? 0 : 1,
              pointerEvents: isPlaying && !isPaused ? 'none' : 'auto'
            }}
          >
            {/* Play Button */}
            {showPlayButton && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 md:w-24 md:h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white/30 transition-transform duration-200 group-hover:scale-110">
                  <svg className="w-10 h-10 md:w-12 md:h-12 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            )}
            
            {/* Video Info */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h3 className="text-white font-semibold text-lg md:text-xl mb-1">{video.title}</h3>
              <p className="text-white/80 text-sm md:text-base">{video.date}</p>
            </div>
          </div>

          {/* Playing Indicator */}
          {isPlaying && !isPaused && (
            <div className="absolute top-6 right-6 bg-rose-500 rounded-full px-4 py-2 flex items-center gap-2 shadow-lg">
              <div className="w-2.5 h-2.5 bg-white rounded-full animate-pulse"></div>
              <span className="text-white text-sm font-medium">Playing</span>
            </div>
          )}

          {/* Paused Indicator */}
          {isPaused && (
            <div className="absolute top-6 right-6 bg-slate-600 rounded-full px-4 py-2 flex items-center gap-2 shadow-lg">
              <span className="text-white text-sm font-medium">Paused</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const PhotoCard: React.FC<{ photo: Photo; index: number }> = ({ photo, index }) => {
    // Check if photo is auto-categorized from folder structure
    const isAutoCategorized = photo.src.includes('/photos/');
    
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        className="group cursor-pointer"
        onClick={() => openLightbox(photo)}
      >
        <div className="relative overflow-hidden rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-300">
          <img
            src={photo.src}
            alt={photo.caption}
            className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
            onError={(e) => {
              // Fallback to root if folder path doesn't exist
              const target = e.target as HTMLImageElement;
              if (photo.src.includes('/photos/')) {
                const filename = photo.src.split('/').pop();
                target.src = `/${filename}`;
              }
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* Category Badge */}
          <div className="absolute top-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 flex items-center gap-2 shadow-lg">
              <span className="text-xs font-semibold text-slate-700">{photo.category}</span>
              {isAutoCategorized && (
                <span className="text-xs" title="Auto-categorized from folder structure">✨</span>
              )}
            </div>
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 p-4 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300 bg-gradient-to-t from-black/80 to-transparent">
            <h3 className="font-medium text-sm mb-1">{photo.caption}</h3>
            <div className="flex items-center justify-between">
              <p className="text-xs opacity-90">{photo.date}</p>
              {isAutoCategorized && (
                <span className="text-xs opacity-75" title="Organized in folder structure">
                  📁
                </span>
              )}
            </div>
          </div>
          
          <div className="absolute top-4 right-4 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen pt-10">
      {/* Hero Section */}
      <section className="relative py-10 px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-slate-800 mb-6">
            Our Gallery
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto ">
            Capturing the beautiful moments that tell our story
          </p>
          {/* <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="max-w-2xl mx-auto"
          >
            <div className="bg-rose-50/80 backdrop-blur-sm rounded-xl px-4 py-3 border border-rose-200/50 inline-flex items-center gap-2 text-sm text-slate-700">
              <span className="text-lg">✨</span>
              <span className="font-medium">Auto-organized by category</span>
              <span className="text-xs text-slate-500">• Photos are automatically categorized from folder structure</span>
            </div>
          </motion.div> */}
          
          {/* <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            onClick={() => setShowUpload(!showUpload)}
            className="btn-primary"
          >
            {showUpload ? 'Hide Upload' : 'Upload Photos'}
          </motion.button> */}
        </motion.div>
      </section>

      {/* Tabs Section */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-center gap-4 mb-2">
            <button
              onClick={() => {
                setActiveTab('photos');
                setShowUpload(false);
              }}
              className={`px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 ${
                activeTab === 'photos'
                  ? 'bg-rose-500 text-white shadow-lg scale-105'
                  : 'bg-amber-50/90 text-slate-700 hover:bg-rose-100 hover:text-rose-700'
              }`}
            >
              📸 Photos
            </button>
            <button
              onClick={() => {
                setActiveTab('videos');
                setShowUpload(false);
              }}
              className={`px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 ${
                activeTab === 'videos'
                  ? 'bg-rose-500 text-white shadow-lg scale-105'
                  : 'bg-amber-50/90 text-slate-700 hover:bg-rose-100 hover:text-rose-700'
              }`}
            >
              🎥 Videos
            </button>
          </div>
        </div>
      </section>

      {/* Photo Upload Section */}
      {/* {showUpload && activeTab === 'photos' && (
        <section className="py-8 px-4">
          <div className="max-w-4xl mx-auto">
            <PhotoUpload onPhotoUpload={handlePhotoUpload} />
          </div>
        </section>
      )} */}

      {/* Category Filter - Only for Photos */}
      {activeTab === 'photos' && (
        <section className="py-8 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-6">
              {/* <h2 className="text-2xl font-semibold text-slate-800 mb-2">Browse by Category</h2> */}
              {/* <p className="text-slate-600 text-sm">
                {filteredPhotos.length} {filteredPhotos.length === 1 ? 'photo' : 'photos'} 
                {selectedCategory !== 'All' && ` in ${selectedCategory}`}
              </p> */}
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => {
                const count = category === 'All' 
                  ? allPhotos.length 
                  : allPhotos.filter(p => p.category === category).length;
                return (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-6 py-3 rounded-full font-medium transition-all duration-300 relative ${
                      selectedCategory === category
                        ? 'bg-rose-500 text-white shadow-lg scale-105'
                        : 'bg-amber-50/90 text-slate-700 hover:bg-rose-100 hover:text-rose-700'
                    }`}
                  >
                    <span>{category}</span>
                    {count > 0 && (
                      <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                        selectedCategory === category
                          ? 'bg-white/20 text-white'
                          : 'bg-rose-100 text-rose-700'
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Photo Grid */}
      {activeTab === 'photos' && (
        <section className="py-8 px-4">
          <div className="max-w-7xl mx-auto">
            {filteredPhotos.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">📸</div>
                <h3 className="text-2xl font-semibold text-slate-800 mb-2">No photos found</h3>
                <p className="text-slate-600">
                  {selectedCategory !== 'All' 
                    ? `No photos in the "${selectedCategory}" category.`
                    : 'No photos available yet.'}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredPhotos.map((photo, index) => (
                    <PhotoCard key={photo.id} photo={photo} index={index} />
                  ))}
                </div>
                {/* Info Footer */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="mt-12 text-center"
                >
                  <div className="inline-flex items-center gap-2 text-sm text-slate-600 bg-slate-50 rounded-full px-4 py-2">
                    {/* <span>💡</span>
                    <span>
                      Photos are automatically organized by folder structure. 
                      {filteredPhotos.some(p => p.src.includes('/photos/')) && (
                        <span className="ml-1 font-medium text-rose-600">
                          {filteredPhotos.filter(p => p.src.includes('/photos/')).length} auto-categorized
                        </span>
                      )}
                    </span> */}
                  </div>
                </motion.div>
              </>
            )}
          </div>
        </section>
      )}

      {/* Video Cards Section */}
      {activeTab === 'videos' && videos.length > 0 && (
        <section className="py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
              {videos.map((video, index) => (
                <VideoCard 
                  key={video.id} 
                  video={video} 
                  index={index}
                  isPlaying={playingVideoId === video.id}
                  isPaused={pausedVideoId === video.id}
                  onPlay={() => {
                    const videoElement = videoRefs.current[video.id];
                    
                    // If this is the paused video, resume it
                    if (playingVideoId === video.id && pausedVideoId === video.id) {
                      if (videoElement) {
                        videoElement.play().catch(error => {
                          console.log('Could not resume video:', error);
                        });
                      }
                      setPausedVideoId(null);
                      return;
                    }
                    
                    // Stop all videos and start playing from this one
                    Object.values(videoRefs.current).forEach(v => {
                      if (v) {
                        v.pause();
                        v.currentTime = 0;
                      }
                    });
                    setPausedVideoId(null);
                    setPlayingVideoId(video.id);
                  }}
                  onPause={() => {
                    const videoElement = videoRefs.current[video.id];
                    if (videoElement) {
                      videoElement.pause();
                      setPausedVideoId(video.id);
                    }
                  }}
                  videoRef={(el) => {
                    videoRefs.current[video.id] = el;
                  }}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      {selectedPhoto && (
        <motion.div
          key="lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 20 }}
              className="relative max-w-5xl max-h-[90vh] w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedPhoto(null)}
                className="absolute top-4 right-4 z-10 w-12 h-12 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-amber-100 hover:bg-black/70 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Navigation Buttons */}
              {filteredPhotos.length > 1 && (
                <>
                  <button
                    onClick={() => navigatePhoto('prev')}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-amber-100 hover:bg-black/70 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => navigatePhoto('next')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-amber-100 hover:bg-black/70 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}

              {/* Photo */}
              <div className="relative">
                <img
                  src={filteredPhotos[currentIndex]?.src}
                  alt={filteredPhotos[currentIndex]?.caption}
                  className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
                />
                
                {/* Photo Info */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
                  <h3 className="text-amber-100 text-xl font-medium mb-3">
                    {filteredPhotos[currentIndex]?.caption}
                  </h3>
                  <div className="flex justify-between items-center">
                    <span className="text-amber-200 text-sm">{filteredPhotos[currentIndex]?.date}</span>
                    <div className="flex items-center gap-2">
                      <div className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-2">
                        <span className="text-amber-100 text-sm font-medium">
                          {filteredPhotos[currentIndex]?.category}
                        </span>
                        {filteredPhotos[currentIndex]?.src.includes('/photos/') && (
                          <span className="text-xs" title="Auto-categorized from folder">✨</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Photo Counter */}
              {filteredPhotos.length > 1 && (
                <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm rounded-full px-4 py-2 text-amber-100 text-sm">
                  {currentIndex + 1} / {filteredPhotos.length}
                </div>
              )}
            </motion.div>
          </motion.div>
      )}
    </div>
  );
};

export default Gallery;
